import { Button, Modal, ModalError, ModalLoading } from '@clearcover/atlas';
import {
  GetPolicyPaymentMethodsResponseType,
  MakePaymentRequestType,
  MakePaymentResponseType,
  PaymentAmountInfoType,
  PaymentMethodType,
} from '@clearcover/core-app-types';
import currency from 'currency.js';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { useAuth0 } from 'Auth';
import { PolicyContext } from 'Policies/utils';
import {
  ConfirmationModal,
  FormInput,
  FormModalContent,
  FormModalFooter,
  FormModalHeader,
} from 'Shared/components';
import { Label } from 'Shared/components/FormInput/Variants';
import { useToast } from 'Shared/components/Toast';
import { useApi, useLazyApi } from 'Shared/services/Api';
import { PolicyPaymentMethod } from '../PolicyPaymentMethod';
import { ItemizedPaymentAmounts } from './ItemizedPayment';
import styles from './styles.module.css';

const PaymentOptions: FC<{
  billingData: MakePaymentResponseType;
  paymentMethodType: 'bank' | 'creditCard';
  convenienceFee: currency;
}> = ({ billingData, paymentMethodType, convenienceFee }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { setFieldValue, values } = useFormikContext<
    MakeOneTimePaymentFormValueType
  >();

  const setPaymentAmountOther = useCallback(
    (rawAmount: string) => {
      const otherAmount = billingData.paymentFeeEnabledState
        ? currency(rawAmount).add(
            billingData.paymentTypeFees[paymentMethodType]
          )
        : currency(rawAmount);

      setFieldValue(
        'paymentAmount',
        convenienceFee.add(otherAmount).toString()
      );
      setFieldValue('otherAmount', rawAmount);
    },
    [billingData, paymentMethodType, setFieldValue, convenienceFee]
  );

  const optionAmount = useCallback(
    function optionAmount(paymentAmountInfo: PaymentAmountInfoType) {
      const rawAmount = billingData.paymentFeeEnabledState
        ? currency(
            paymentMethodType === 'bank'
              ? paymentAmountInfo.minimumPaymentUsingBank
              : paymentAmountInfo.minimumPaymentUsingCreditCard
          )
        : currency(paymentAmountInfo.remainingBalance);
      return rawAmount.value === 0 ? rawAmount : rawAmount.add(convenienceFee);
    },
    [billingData.paymentFeeEnabledState, convenienceFee, paymentMethodType]
  );
  const overduePayment = useMemo(() => optionAmount(billingData.overdue), [
    optionAmount,
    billingData.overdue,
  ]);
  const minimumPayment = useMemo(() => optionAmount(billingData.minimum), [
    optionAmount,
    billingData.minimum,
  ]);
  const remainingPayment = useMemo(() => optionAmount(billingData.remaining), [
    optionAmount,
    billingData.remaining,
  ]);

  // One-time initialization to ensure added fees are included in default payment amount
  useEffect(() => {
    setFieldValue('paymentAmount', overduePayment.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.paymentOptions}>
      <div
        className={styles.paymentOption}
        data-testid="payment-amount-overdue"
      >
        <FormInput
          name="overdue-payment"
          value={'overdue'}
          checked={values.paymentOption === 'overdue'}
          variant="radio"
          label="Amount overdue"
          onChecked={() => {
            setFieldValue('paymentOption', 'overdue');
            setFieldValue('paymentAmount', overduePayment.toString());
          }}
        />
        <div data-testid="radio-overdue-payment-value" className={styles.value}>
          {overduePayment.format(true)}
        </div>
      </div>

      <div
        className={styles.paymentOption}
        data-testid="payment-amount-minimum"
      >
        <FormInput
          name="minimum-payment"
          value={'minimum'}
          checked={values.paymentOption === 'minimum'}
          variant="radio"
          label="Total payment due"
          onChecked={() => {
            setFieldValue('paymentOption', 'minimum');
            setFieldValue('paymentAmount', minimumPayment.toString());
          }}
        />
        <div data-testid="radio-minimum-payment-value" className={styles.value}>
          {minimumPayment.format(true)}
        </div>
      </div>

      <div
        className={styles.paymentOption}
        data-testid="payment-amount-remaining"
      >
        <FormInput
          name="remaining-payment"
          value={'remaining'}
          checked={values.paymentOption === 'remaining'}
          variant="radio"
          label="Total balance"
          onChecked={() => {
            setFieldValue('paymentOption', 'remaining');
            setFieldValue('paymentAmount', remainingPayment.toString());
          }}
        />
        <div
          data-testid="radio-remaining-payment-value"
          className={styles.value}
        >
          {remainingPayment.format(true)}
        </div>
      </div>

      <div className={styles.paymentOption} data-testid="payment-amount-other">
        <FormInput
          name="payment-amount-other"
          value={'other'}
          checked={values.paymentOption === 'other'}
          variant="radio"
          label="Other amount"
          onChecked={() => {
            inputRef?.current?.focus();
            setFieldValue('paymentOption', 'other');
            setPaymentAmountOther(values.otherAmount);
          }}
        />
        <div className={styles.value}>
          <input
            type="text"
            value={values.otherAmount}
            ref={inputRef}
            onChange={e => {
              if (values.paymentOption !== 'other')
                setFieldValue('paymentOption', 'other');
              setPaymentAmountOther(e.currentTarget.value);
            }}
            data-testid="payment-amount-other-input"
            className={styles.otherAmountInput}
          />
        </div>
      </div>
    </div>
  );
};

function findPaymentMethod({
  paymentMethods,
  paymentMethodId,
}: {
  paymentMethods: Array<PaymentMethodType>;
  paymentMethodId: string;
}): PaymentMethodType {
  const paymentMethod = paymentMethods.find(pm => pm.id === paymentMethodId);
  if (!paymentMethod) {
    throw Error('Payment method not found');
  }
  return paymentMethod;
}

export interface MakeOneTimePaymentFormProps {
  close: () => void;
}

const MAKE_ONE_TIME_PAYMENT_SCHEMA = Yup.object({
  paymentOption: Yup.string().required('Required'),
  paymentMethodId: Yup.string().required('Required'),
  paymentAmount: Yup.number().required('Required'),
});

export type MakeOneTimePaymentFormValueType = {
  paymentOption: 'overdue' | 'minimum' | 'remaining' | 'other';
  paymentMethodId: string;
  paymentAmount: string;
  otherAmount: string;
};

export const MakeOneTimePaymentForm: FC<MakeOneTimePaymentFormProps> = ({
  close,
}) => {
  const { toast } = useToast();
  const { id: policyId } = useParams<{
    id: string;
  }>();

  const {
    refetchHandler,
    details: { region, termId },
  } = useContext(PolicyContext);

  const [{ data, loading: optionsLoading, error: optionsError }] = useApi<
    MakePaymentResponseType
  >(`payments/${policyId}/options?region=${region}&termId=${termId}`);

  const [
    {
      data: policyPaymentMethods,
      loading: paymentMethodsLoading,
      error: paymentMethodsError,
    },
  ] = useApi<GetPolicyPaymentMethodsResponseType>(
    `/policies/${policyId}/paymentMethods`
  );
  const { user: currentUser } = useAuth0();
  const convenienceFee = useMemo(
    () =>
      currency(
        currentUser?.isInternalUser
          ? data?.paymentTypeFees?.convenienceFee
          : '0'
      ),
    [data?.paymentTypeFees?.convenienceFee, currentUser?.isInternalUser]
  );

  const [, makeOneTimePayment] = useLazyApi<MakePaymentRequestType>({
    method: 'POST',
    url: `/policies/${policyId}/makePayment`,
  });

  const handleFormSubmit = useCallback(
    (
      values: MakeOneTimePaymentFormValueType,
      {
        setStatus,
        setSubmitting,
      }: FormikHelpers<MakeOneTimePaymentFormValueType>
    ) => {
      return makeOneTimePayment({
        data: {
          paymentMethodId: values.paymentMethodId,
          paymentTotal: parseFloat(values.paymentAmount),
          termId,
          chargeConvenienceFee: convenienceFee.value !== 0,
        },
      })
        .then(() => {
          toast({
            message: 'Payment made successfully.',
            type: 'success',
            position: 'bottom',
            withSecondaryNav: false,
          });

          refetchHandler();
          close();
        })
        .catch(err => {
          toast({
            message: err.response.data.message ?? err.message,
            type: 'error',
            position: 'top',
            withSecondaryNav: false,
            width: 'full',
            icon: 'info',
          });
          setSubmitting(false);
          setStatus(err.response.data.message ?? err.message);
        });
    },
    [
      makeOneTimePayment,
      convenienceFee.value,
      termId,
      toast,
      refetchHandler,
      close,
    ]
  );

  if (optionsLoading || paymentMethodsLoading) {
    return <ModalLoading data-testid="otpayment-loading" />;
  }

  const initializationError = optionsError || paymentMethodsError;

  if (!!initializationError) {
    return (
      <ModalError
        data-testid="otpayment-error"
        errorMessage={initializationError.message}
        componentStack={initializationError.stack}
      />
    );
  }

  const INITIAL_FORM_VALUES: MakeOneTimePaymentFormValueType = {
    paymentOption: 'overdue',
    paymentMethodId: policyPaymentMethods.defaultPaymentMethod.id,
    paymentAmount: data.overdue.minimumPayment,
    otherAmount: '0',
  };

  return (
    <Formik
      initialValues={INITIAL_FORM_VALUES}
      onSubmit={handleFormSubmit}
      validationSchema={MAKE_ONE_TIME_PAYMENT_SCHEMA}
      validateOnMount
    >
      {({ setFieldValue, isValid, values, isSubmitting, handleSubmit }) => {
        const totalAmount = currency(values.paymentAmount, {
          formatWithSymbol: true,
        });
        const paymentMethod = findPaymentMethod({
          paymentMethods: policyPaymentMethods.paymentMethods,
          paymentMethodId: values.paymentMethodId,
        });
        const chosenSuggestion =
          values.paymentOption === 'other'
            ? {
                productAmount: '0',
                installmentFee: '0',
                productFees: '0',
                waivedFees: '0',
                remainingBalance: '0',
                memberSurplusContribution: '0',
                taxesTotal: '0',
              }
            : data[values.paymentOption];
        const noPaymentDue =
          totalAmount.value === 0 ||
          (values.paymentOption === 'other' &&
            currency(values.otherAmount).value === 0);

        return (
          <>
            <FormModalHeader>Make a Payment</FormModalHeader>
            <FormModalContent>
              <div className={styles.section}>
                <h1>Make a payment</h1>
                <PolicyPaymentMethod
                  policyPaymentMethods={policyPaymentMethods.paymentMethods}
                  value={values.paymentMethodId}
                  setValue={value => setFieldValue('paymentMethodId', value)}
                />
              </div>
              <div className={styles.section}>
                <Label>Select payment amount</Label>
                <PaymentOptions
                  billingData={data}
                  paymentMethodType={
                    findPaymentMethod({
                      paymentMethods: policyPaymentMethods.paymentMethods,
                      paymentMethodId: values.paymentMethodId,
                    }).type
                  }
                  convenienceFee={convenienceFee}
                />

                <ItemizedPaymentAmounts
                  chosenPaymentMethodType={paymentMethod.type}
                  paymentFeeEnabledState={data.paymentFeeEnabledState}
                  paymentTypeFees={data.paymentTypeFees}
                  itemizedAmounts={chosenSuggestion}
                  totalAmount={totalAmount}
                />
              </div>
            </FormModalContent>
            <FormModalFooter
              showStatus={false}
              close={close}
              checkDirty={false}
              identifier={`${values.paymentMethodId}-make-payment`}
              confirmationModal={
                <Modal
                  variant="normal"
                  toggle={openModal => (
                    <Button
                      data-testid="one-time-payment-exit"
                      size={'md'}
                      variant="primary"
                      type="button"
                      onClick={openModal}
                      disabled={isSubmitting || noPaymentDue}
                    >
                      Continue
                    </Button>
                  )}
                >
                  {close => (
                    <ConfirmationModal
                      onSubmit={() => handleSubmit()}
                      close={close}
                      disabled={!isValid}
                      isSubmitting={isSubmitting}
                      variant="primary"
                      headerText="Confirm one time payment"
                      bodyText={`This will be a one time payment of ${currency(
                        values.paymentAmount,
                        {
                          formatWithSymbol: true,
                        }
                      ).format()}. Please confirm.`}
                    />
                  )}
                </Modal>
              }
            />
          </>
        );
      }}
    </Formik>
  );
};

MakeOneTimePaymentForm.displayName = 'MakeOneTimePaymentForm';
